import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import BoltIcon from '@mui/icons-material/Bolt'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import CalculateIcon from '@mui/icons-material/Calculate'
import RefreshIcon from '@mui/icons-material/Refresh'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { makeGetRequest, makePostRequest } from 'utils/api'

// Traduit les libellés techniques de `data_status.missing_data` (renvoyés par le
// back, en anglais) en actions concrètes en français pour inviter l'utilisateur
// à compléter. Les entrées purement techniques (module indisponible, erreurs)
// sont filtrées : elles n'ont pas de sens côté utilisateur.
function humanizeMissing(missing) {
  if (!Array.isArray(missing)) return []
  const out = []
  missing.forEach((raw) => {
    const s = String(raw || '')
    const low = s.toLowerCase()
    // Entrées purement techniques (module indisponible, erreurs) : non pertinentes
    // côté utilisateur, on les masque.
    if (low.includes('not available') || low.includes('module') || low.startsWith('error')) return
    let label = null
    if (low.includes('bank account') || low.includes('financial account')) label = 'Ajouter un compte bancaire'
    else if (low.includes('financial operation')) label = 'Enregistrer des opérations financières récentes'
    else if (low.includes('home-related') || low.includes('home expense')) label = 'Enregistrer vos dépenses logement (loyer, charges, entretien…)'
    else if (low.includes('carbon footprint') || low.includes('carbon') || low.includes('footprint') || low.includes('emission')) label = 'Calculer votre empreinte carbone'
    else if (low.includes('social data')) label = 'Ajouter des contacts ou planifier des événements'
    else if (low.includes('professional data')) label = 'Renseigner votre parcours pro ou planifier des événements pro'
    else if (low.includes('time tracking event')) label = 'Planifier des événements récents'
    else if (low.includes('personal event')) label = 'Planifier des événements perso (santé, sport, loisirs)'
    else if (low.includes('event')) label = 'Planifier des événements récents'
    else if (low.includes('task')) label = 'Ajouter des tâches avec échéance'
    else if (low.includes('contact')) label = 'Ajouter des contacts'
    else if (low.includes('administrative document') || low.includes('document')) label = 'Suivre au moins un document administratif'
    else label = s
    if (label && !out.includes(label)) out.push(label)
  })
  return out
}

// Couleur d'un score 0-10 (rouge -> orange -> vert), pour les jauges d'aspects.
function scoreColor(score) {
  if (score == null) return 'rgba(255,255,255,0.3)'
  if (score < 4) return '#ff8a80'
  if (score < 7) return '#ffcc80'
  return '#a5d6a7'
}

// Interprétation textuelle d'une note de domaine (0-10).
function scoreBand(score, t) {
  if (score == null) return t('lifeReport.domainDetail.bandUnknown', 'Non calculée')
  if (score < 4) return t('lifeReport.domainDetail.bandLow', 'À renforcer')
  if (score < 7) return t('lifeReport.domainDetail.bandMid', 'Correct, peut mieux faire')
  if (score < 8.5) return t('lifeReport.domainDetail.bandGood', 'Bon niveau')
  return t('lifeReport.domainDetail.bandGreat', 'Excellent')
}

// Libellés lisibles des domaines (alignés sur GeneralReportEnhanced / le back).
const DOMAIN_LABELS = {
  Finance: 'Finance',
  Temps: 'Temps',
  Earth: 'Planète',
  Social: 'Social',
  Professionnel: 'Professionnel',
  Personnel: 'Personnel',
  Domicile: 'Domicile',
  Administratif: 'Administratif',
}

const ALLOWED_DOMAINS = Object.keys(DOMAIN_LABELS)

// Deux paliers IA proposés à l'utilisateur (mêmes alias que le backend).
const TIERS = [
  {
    key: 'economic',
    label: 'Éco',
    icon: <BoltIcon fontSize="small" />,
    description: 'Rapide et économique (gpt-4o-mini)',
  },
  {
    key: 'premium',
    label: 'Premium',
    icon: <WorkspacePremiumIcon fontSize="small" />,
    description: 'Analyse plus fine (gpt-4o)',
  },
]

/**
 * Sous-page détaillée d'un domaine du Life Résumé.
 *
 * Explique la note du domaine (ce qui la plombe, comment l'améliorer, un
 * commentaire général relatif aux objectifs). Le contenu est généré par IA et
 * consomme des crédits au 1er accès du jour. La page affiche d'abord un écran de
 * confirmation (coût + choix éco/premium) AVANT de générer/débiter ; un rapport
 * déjà généré aujourd'hui (cache) est renvoyé sans reconsommer.
 */
export default function DomainDetailPage() {
  const { code } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [tier, setTier] = useState('economic')
  const [peek, setPeek] = useState(null) // { cached, cost_estimate, report, ... }
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true) // chargement initial (peek)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)

  const isValidDomain = ALLOWED_DOMAINS.includes(code)
  const domainLabel = DOMAIN_LABELS[code] || code

  const fetchPeek = useCallback(
    async (selectedTier) => {
      setLoading(true)
      setError(null)
      try {
        const res = await makeGetRequest(
          `/api/general/life-resume/domain-detail/${code}/?tier=${selectedTier}`,
          {},
          true,
        )
        if (res?.status === 200) {
          setPeek(res.data)
          if (res.data?.cached && res.data?.report) {
            setReport(res.data.report)
          }
        } else {
          setError(res?.data?.detail || t('common.errorGeneric', 'Une erreur est survenue.'))
        }
      } catch (e) {
        setError(t('common.errorGeneric', 'Une erreur est survenue.'))
      } finally {
        setLoading(false)
      }
    },
    [code, t],
  )

  useEffect(() => {
    if (isValidDomain) {
      fetchPeek(tier)
    } else {
      setLoading(false)
    }
    // On ne recharge le peek que sur changement de domaine (le coût par tier
    // est recalculé localement à l'affichage). eslint-disable-next-line
  }, [code, isValidDomain]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = useCallback(
    async (force = false) => {
      setGenerating(true)
      setError(null)
      try {
        const res = await makePostRequest(
          `/api/general/life-resume/domain-detail/${code}/generate/`,
          { tier, force },
          {},
          true,
        )
        if (res?.status === 200) {
          setReport(res.data)
          setPeek((prev) => (prev ? { ...prev, cached: true, report: res.data } : prev))
        } else if (res?.status === 402) {
          // La modale globale "Crédits insuffisants" est ouverte par
          // l'intercepteur axios ; on affiche aussi un rappel inline.
          setError(
            t(
              'lifeReport.domainDetail.insufficientCredits',
              'Crédits insuffisants pour générer ce rapport.',
            ),
          )
        } else {
          setError(res?.data?.detail || t('common.errorGeneric', 'Une erreur est survenue.'))
        }
      } catch (e) {
        setError(t('common.errorGeneric', 'Une erreur est survenue.'))
      } finally {
        setGenerating(false)
      }
    },
    [code, tier, t],
  )

  const cost = peek?.cost_estimate?.cost

  // ---- Rendu ---------------------------------------------------------------

  if (!isValidDomain) {
    return (
      <Box sx={{ p: 4, color: 'white', minHeight: '100vh', bgcolor: '#0f1c2e' }}>
        <BackButton onClick={() => navigate('/life-resume')} label={t('common.back', 'Retour')} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          {t('lifeReport.domainDetail.unknownDomain', 'Domaine inconnu.')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, color: 'white', minHeight: '100vh', bgcolor: '#0f1c2e' }}>
      <BackButton onClick={() => navigate('/life-resume')} label={t('common.back', 'Retour')} />

      {/* En-tête : domaine + note */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {domainLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {t('lifeReport.domainDetail.subtitle', 'Analyse détaillée de votre note du jour')}
          </Typography>
        </Box>
        {report?.domain_score !== null && report?.domain_score !== undefined && (
          <Typography variant="h3" sx={{ color: '#fbca6d', fontWeight: 700 }}>
            {Number(report.domain_score).toLocaleString('fr-FR', {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
          </Typography>
        )}
      </Box>

      {/* État « en attente de données » : la note du domaine est plancher (0) ou
          partielle faute de données récentes. On invite explicitement à compléter,
          au lieu de laisser croire à une note figée. */}
      {!loading && peek?.data_status && peek.data_status.ready === false && (
        <DataStatusView
          dataStatus={peek.data_status}
          score={peek?.estimatif?.score}
          t={t}
        />
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <ReportView report={report} t={t} onRegenerate={() => handleGenerate(true)} generating={generating} />
      ) : (
        <Box display="flex" flexDirection="column" gap={2} sx={{ maxWidth: 820 }}>
          {/* Estimatif gratuit (sans IA) affiché avant génération. */}
          <EstimatifView estimatif={peek?.estimatif} score={peek?.estimatif?.score} t={t} />
          <ConfirmView
            t={t}
            tier={tier}
            setTier={setTier}
            cost={cost}
            generating={generating}
            onGenerate={() => handleGenerate(false)}
            error={error}
          />
        </Box>
      )}

      {/* Activités en cours rattachées au domaine (indépendant du rapport IA). */}
      {!loading && <ActivitiesView activities={peek?.activities} t={t} />}

      {report && error && (
        <Typography variant="body2" sx={{ color: '#ff8a80', mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}

function BackButton({ onClick, label }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<ArrowBackIcon />}
      sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'none' }}
    >
      {label}
    </Button>
  )
}

/**
 * Écran de confirmation affiché AVANT toute génération/débit : coût + choix du
 * palier IA + bouton de génération.
 */
function ConfirmView({ t, tier, setTier, cost, generating, onGenerate, error }) {
  return (
    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', maxWidth: 640 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <AutoAwesomeIcon sx={{ color: '#fbca6d' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('lifeReport.domainDetail.confirmTitle', 'Générer le rapport détaillé')}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
          {t(
            'lifeReport.domainDetail.confirmBody',
            "Ce rapport est généré par IA et consomme des crédits. Une fois généré aujourd'hui, le consulter à nouveau dans la journée est gratuit ; un nouveau rapport sera généré demain.",
          )}
        </Typography>

        {/* Choix du palier IA */}
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}>
          {t('lifeReport.domainDetail.chooseTier', 'Choisissez la qualité de génération :')}
        </Typography>
        <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
          {TIERS.map((opt) => {
            const selected = tier === opt.key
            return (
              <Card
                key={opt.key}
                onClick={() => setTier(opt.key)}
                sx={{
                  cursor: 'pointer',
                  flex: '1 1 220px',
                  bgcolor: selected ? 'rgba(251,202,109,0.15)' : 'rgba(255,255,255,0.04)',
                  border: selected ? '2px solid #fbca6d' : '1px solid rgba(255,255,255,0.12)',
                  transition: 'all 0.15s',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {opt.icon}
                    <Typography sx={{ fontWeight: 700 }}>{opt.label}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {opt.description}
                  </Typography>
                </CardContent>
              </Card>
            )
          })}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Chip
            label={
              cost != null
                ? t('lifeReport.domainDetail.cost', { defaultValue: 'Coût : {{cost}} crédit(s)', cost })
                : t('lifeReport.domainDetail.costUnknown', 'Coût indisponible')
            }
            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 600 }}
          />
          <Button
            variant="contained"
            onClick={onGenerate}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            sx={{ bgcolor: '#fbca6d', color: '#0f1c2e', fontWeight: 700, '&:hover': { bgcolor: '#f5b93f' } }}
          >
            {generating
              ? t('lifeReport.domainDetail.generating', 'Génération…')
              : t('lifeReport.domainDetail.generate', 'Générer le rapport')}
          </Button>
        </Box>

        {error && (
          <Typography variant="body2" sx={{ color: '#ff8a80', mt: 2 }}>
            {error}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

/** Affichage du rapport généré (ou servi depuis le cache). */
function ReportView({ report, t, onRegenerate, generating }) {
  const actionItems = report?.structured_json?.action_items || []
  const estimatif = report?.structured_json?.estimatif || null
  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ maxWidth: 820 }}>
      <EstimatifView estimatif={estimatif} score={report?.domain_score} t={t} />
      <Section
        icon={<ChatBubbleOutlineIcon sx={{ color: '#90caf9' }} />}
        title={t('lifeReport.domainDetail.generalComment', 'Commentaire général')}
        content={report.general_comment}
      />
      <Section
        icon={<TrendingDownIcon sx={{ color: '#ff8a80' }} />}
        title={t('lifeReport.domainDetail.whatDragsDown', 'Ce qui plombe la note')}
        content={report.what_drags_down}
      />
      <Section
        icon={<TipsAndUpdatesIcon sx={{ color: '#a5d6a7' }} />}
        title={t('lifeReport.domainDetail.howToImprove', 'Comment améliorer')}
        content={report.how_to_improve}
      />

      {actionItems.length > 0 && (
        <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t('lifeReport.domainDetail.actionItems', 'Actions concrètes')}
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {actionItems.map((item, idx) => (
                <li key={idx}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 0.5 }}>
                    {item}
                  </Typography>
                </li>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Box display="flex" alignItems="center" gap={2} mt={1}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          {t('lifeReport.domainDetail.cachedNote', "Rapport du jour — le régénérer consomme à nouveau des crédits.")}
        </Typography>
        <Button
          size="small"
          onClick={onRegenerate}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'none' }}
        >
          {t('lifeReport.domainDetail.regenerate', 'Régénérer')}
        </Button>
      </Box>
    </Box>
  )
}

/**
 * Bandeau « en attente de données ». Affiché quand le domaine n'a pas (encore)
 * de données récentes suffisantes : la note du jour est plancher/partielle. On
 * explique pourquoi et on liste les actions concrètes à faire pour que la note
 * soit réellement recalculée (au lieu de rester à 0).
 */
function DataStatusView({ dataStatus, score, t }) {
  const actions = humanizeMissing(dataStatus?.missing_data)
  const windowDays = dataStatus?.window_days || 30
  const isZero = score != null && Number(score) === 0

  return (
    <Card
      sx={{
        bgcolor: 'rgba(251, 202, 109, 0.08)',
        border: '1px solid rgba(251, 202, 109, 0.35)',
        mb: 2,
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <HourglassEmptyIcon sx={{ color: '#fbca6d' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('lifeReport.domainDetail.awaitingTitle', 'En attente de données')}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mb: actions.length ? 2 : 0 }}>
          {isZero
            ? t(
                'lifeReport.domainDetail.awaitingBodyZero',
                `Ce domaine n'a aucune donnée exploitable sur les ${windowDays} derniers jours : sa note est donc de 0. Complétez les éléments ci-dessous pour qu'elle soit calculée à partir de votre activité réelle.`,
              )
            : t(
                'lifeReport.domainDetail.awaitingBodyPartial',
                `Il manque des données récentes (${windowDays} derniers jours) pour calculer pleinement cette note. Complétez les éléments ci-dessous pour qu'elle reflète votre activité réelle.`,
              )}
        </Typography>

        {actions.length > 0 && (
          <Box display="flex" flexDirection="column" gap={1}>
            {actions.map((action, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1}>
                <AddCircleOutlineIcon sx={{ color: '#fbca6d', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {action}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * « Estimatif » : décompose la note du domaine pour la rendre compréhensible.
 * Affiche la note en grand + son interprétation, puis, si des aspects sont
 * disponibles, la contribution pondérée de chaque aspect (jauge 0-10 + poids %).
 */
function EstimatifView({ estimatif, score, t }) {
  const effectiveScore =
    score != null ? Number(score) : estimatif && estimatif.score != null ? Number(estimatif.score) : null
  const breakdown = (estimatif && estimatif.breakdown) || []
  const hasTraces = estimatif && estimatif.has_traces && breakdown.length > 0

  return (
    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CalculateIcon sx={{ color: '#fbca6d' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('lifeReport.domainDetail.estimatifTitle', 'Estimatif de la note')}
          </Typography>
        </Box>

        {/* Note + interprétation */}
        <Box display="flex" alignItems="baseline" gap={1.5} mb={hasTraces ? 2 : 1}>
          <Typography variant="h3" sx={{ color: '#fbca6d', fontWeight: 800, lineHeight: 1 }}>
            {effectiveScore != null
              ? effectiveScore.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
              : '—'}
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 400 }}>
            / 10
          </Typography>
          <Chip
            size="small"
            label={scoreBand(effectiveScore, t)}
            sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 600 }}
          />
        </Box>

        {hasTraces ? (
          <>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1.5 }}>
              {t(
                'lifeReport.domainDetail.estimatifIntro',
                "Votre note est une moyenne pondérée des aspects ci-dessous. Le poids indique l'importance de l'aspect dans le calcul.",
              )}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.25}>
              {breakdown.map((b, idx) => (
                <AspectBar key={idx} item={b} t={t} />
              ))}
            </Box>
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {t(
              'lifeReport.domainDetail.estimatifNoTraces',
              "Le détail chiffré par aspect n'est pas encore disponible pour ce domaine : la note est reportée de votre dernière évaluation. Elle se précisera dès que de nouvelles données seront saisies.",
            )}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

/** Une ligne de l'estimatif : aspect, jauge de score 0-10 et poids en %. */
function AspectBar({ item, t }) {
  const score = item?.score
  const pct = score != null ? Math.max(0, Math.min(100, (Number(score) / 10) * 100)) : 0
  const weightPct = item?.weight_pct
  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.25}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
          {item?.aspect || '—'}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {weightPct != null && (
            <Tooltip
              title={t('lifeReport.domainDetail.weightTooltip', "Poids de l'aspect dans la note")}
              arrow
            >
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                {weightPct}%
              </Typography>
            </Tooltip>
          )}
          <Typography variant="caption" sx={{ color: scoreColor(score), fontWeight: 700, minWidth: 34, textAlign: 'right' }}>
            {score != null
              ? Number(score).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
              : '—'}
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': { bgcolor: scoreColor(score), borderRadius: 3 },
        }}
      />
    </Box>
  )
}

// Icône par type d'activité.
const ACTIVITY_ICONS = {
  objective: <FlagOutlinedIcon fontSize="small" sx={{ color: '#90caf9' }} />,
  action: <BoltOutlinedIcon fontSize="small" sx={{ color: '#fbca6d' }} />,
  project: <FolderOutlinedIcon fontSize="small" sx={{ color: '#ce93d8' }} />,
  task: <AssignmentOutlinedIcon fontSize="small" sx={{ color: '#a5d6a7' }} />,
  event: <EventOutlinedIcon fontSize="small" sx={{ color: '#80cbc4' }} />,
  roadmap: <ExploreOutlinedIcon fontSize="small" sx={{ color: '#ffab91' }} />,
}

function formatDueDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

/**
 * « Activités en cours » : objectifs, actions, projets, tâches, événements et
 * cap de vie rattachés au domaine (ce sur quoi repose la notation). Regroupé par
 * type, lecture seule. N'affiche rien si aucune activité.
 */
function ActivitiesView({ activities, t }) {
  const groups = (activities && activities.groups) || []
  const total = (activities && activities.total) || 0
  if (!groups.length) {
    return (
      <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', mt: 2, maxWidth: 820 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('lifeReport.domainDetail.activitiesTitle', 'Activités en cours')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {t(
              'lifeReport.domainDetail.activitiesEmpty',
              "Aucune activité en cours rattachée à ce domaine pour l'instant.",
            )}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', mt: 2, maxWidth: 820 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('lifeReport.domainDetail.activitiesTitle', 'Activités en cours')}
          </Typography>
          <Chip
            size="small"
            label={total}
            sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white', fontWeight: 700, height: 20 }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mb: 1.5 }}>
          {t(
            'lifeReport.domainDetail.activitiesSubtitle',
            "Ce qui alimente la note de ce domaine : objectifs, actions, projets, tâches, événements et cap de vie.",
          )}
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          {groups.map((g) => (
            <Box key={g.key}>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {g.label} ({(g.items || []).length})
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.75} mt={0.75}>
                {(g.items || []).map((item) => (
                  <ActivityRow key={`${item.type}-${item.id}`} item={item} t={t} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  )
}

/** Une ligne d'activité : icône + titre + méta (échéance / avancement / statut). */
function ActivityRow({ item, t }) {
  const due = formatDueDate(item?.due_date)
  const hasProgress = item?.progress != null
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{
        p: 1,
        borderRadius: 1.5,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {ACTIVITY_ICONS[item.type] || <AssignmentOutlinedIcon fontSize="small" />}
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {item.title}
        </Typography>
        {(item?.meta?.project || item?.meta?.category) && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
            {item.meta.project || item.meta.category}
          </Typography>
        )}
      </Box>
      {hasProgress && (
        <Chip
          size="small"
          label={`${Math.round(item.progress)}%`}
          sx={{ bgcolor: 'rgba(165,214,167,0.18)', color: '#a5d6a7', fontWeight: 700, height: 20 }}
        />
      )}
      {due && (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' }}>
          {due}
        </Typography>
      )}
    </Box>
  )
}

function Section({ icon, title, content }) {
  if (!content) return null
  return (
    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          {icon}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
          {content}
        </Typography>
      </CardContent>
    </Card>
  )
}
